import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useKlasisTracker from "../utils/klasisTracker";
import _ from "lodash";
import PageContent from "../parts/page-content/PageContent";
import { useParams } from "react-router-dom";
import { RestService } from "../utils/axios";
import { ApiRoutes } from "../configuration/apiRoutes";
import { Skeleton, Stack, Typography } from "@mui/material";
import { Translations } from "../localization/translations";

import { SettingGroupType } from "../models/SettingGroupType";
import { SystemConfigParameterDto } from "../models/SystemConfigParameterDto";

interface IParameter {
    titleName: string;
    textName: string;
}

const EasyToReadPage: React.FC = () => {
    const { locale } = useParams();

    const { t } = useTranslation();
    const { klasisTrackPageView } = useKlasisTracker();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [response, setResponse] = useState<SystemConfigParameterDto[]>();

    useEffect(() => {
        klasisTrackPageView();
    }, [klasisTrackPageView]);

    useEffect(() => {
        if (!locale) {
            return;
        }

        setIsLoading(true);

        RestService.Get<SystemConfigParameterDto[]>(
            ApiRoutes.SystemConfig.GetParameters({
                type: SettingGroupType.frontOfficeEasyToRead,
                localeCode: locale,
            }),
            (response) => {
                setResponse(response);
            },
            (error) => {
                console.log(error);
            },
            () => {
                setIsLoading(false);
            }
        );
    }, [locale]);

    const parameters: IParameter[] = _.range(1, 10).map((number) => ({
        titleName: `pl_easy_read_title_${("0" + number).slice(-2)}`,
        textName: `pl_easy_read_text_${("0" + number).slice(-2)}`,
    }));

    return (
        <PageContent
            title={t(Translations.pages.title.easyToRead)}
            showBreadcrumbs={true}
            showTitleBarCommands={true}
        >
            <Stack spacing={4}>
                {isLoading && (
                    <>
                        <EasyToReadBlock isLoading={isLoading} />
                        <EasyToReadBlock isLoading={isLoading} />
                    </>
                )}

                {!isLoading &&
                    _.map(parameters, (parameter, index) => {
                        const title = _.find(
                            response,
                            (record) => record.name === parameter.titleName
                        )?.value;
                        const text = _.find(
                            response,
                            (record) => record.name === parameter.textName
                        )?.value;

                        if (!title && !text) {
                            return null;
                        }

                        return (
                            <EasyToReadBlock
                                key={index}
                                title={title}
                                text={text}
                            />
                        );
                    })}
            </Stack>
        </PageContent>
    );
};

interface IEasyToReadBlockProps {
    title?: string;
    text?: string;
    isLoading?: boolean;
}

const EasyToReadBlock: React.FC<IEasyToReadBlockProps> = ({
    title,
    text,
    isLoading,
}) => {
    return (
        <Stack spacing={1}>
            {(title || isLoading) && (
                <Typography variant="h5" fontWeight={700}>
                    {isLoading ? <Skeleton width={300} /> : title}
                </Typography>
            )}
            {(text || isLoading) && (
                <Typography variant="body1">
                    {isLoading ? (
                        <>
                            <Skeleton />
                            <Skeleton width={800} />
                        </>
                    ) : (
                        text
                    )}
                </Typography>
            )}
        </Stack>
    );
};

export default EasyToReadPage;
